import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  ErrorHandler,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatTooltip } from '@angular/material/tooltip';
import { CrudSecureActions } from '@deprecated/api-interfaces';
import {
  ClipboardDirective,
  ColumnComponent,
  ColumnHeaderDirective,
  ColumnTemplateDirective,
  PaginatorDirective,
  SelectionDirective,
  SpinnerComponent,
  SpxObservableDataSource,
  SpxSelectionModel,
  TableComponent,
} from '@ird/ng-base';
import { IrdFormatPipe, translationsVersion } from '@ird/ng-core';
import { IrdPropertyType, IrdResolvedProperty, IrdTableMetadata, PAGE_SIZE_OPTIONS } from '@ird/shared-base';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslatePipe } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { ClipboardMenuItem, GenericAdminOptions, RowAction, SelectOptions } from '../../models';
import { ActionsService } from '../../services';
import { EditPopoverComponent, EditPopoverConfig } from '../edit-popover';
import { PropertyValueComponent } from '../property-value';

@Component({
  selector: 'shared-generic-table',
  templateUrl: './generic-table.component.html',
  styleUrls: ['./generic-table.component.scss'],
  imports: [
    CommonModule,
    MatCard,
    MatCardContent,
    SpinnerComponent,
    CdkVirtualScrollViewport,
    TableComponent,
    ColumnComponent,
    TranslatePipe,
    IrdFormatPipe,
    EditPopoverComponent,
    PropertyValueComponent,
    MatTooltip,
    MatPaginator,
    PaginatorDirective,
    FlexLayoutModule,
    ScrollingModule,
    ColumnHeaderDirective,
    ColumnTemplateDirective,
    SelectionDirective,
    ClipboardDirective,
    MatIcon,
    MatButton,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
})
export class GenericTableComponent<Dto> implements OnInit, OnChanges, OnDestroy {
  /** The edit popover has no editor for these, nor for a list; the edit dialog changes them. */
  private static readonly NOT_INLINE_EDITABLE: ReadonlySet<IrdPropertyType> = new Set<IrdPropertyType>(['Object', 'icon']);
  /** Edited in place by picking one of a list of values — which the page or the schema has to supply. */
  private static readonly CHOICE_TYPES: ReadonlySet<IrdPropertyType> = new Set<IrdPropertyType>(['select', 'enum']);

  @Input() isLoading: boolean;
  @Input() dataSource: SpxObservableDataSource<any>;
  /**
   * The columns, their order and their cells, already resolved from the entity's table declaration.
   */
  @Input({ required: true }) metadata: IrdTableMetadata<Dto>;
  /** Keeps the edit row action next to inline editing. */
  @Input() withEditDialog: boolean = false;
  @Input() options: GenericAdminOptions;
  @Input() withActions: boolean = true;
  @Input() crudSecureActions: CrudSecureActions;
  @Input() enableSelection: boolean = false;
  @Input() previewRows: Set<unknown> = new Set();
  @Input() clipboardMenuItems: ClipboardMenuItem[] = [];
  @Input() clipboardColumnKeys: string[] = [];
  @Input() clipboardDefaultValues: unknown = {};
  @Input() clipboardEnableCopy: boolean = true;
  @Input() clipboardEnablePaste: boolean = true;
  @Input() inlineRowActions: RowAction<Dto>[] = [];
  @Input() menuRowActions: RowAction<Dto>[] = [];

  @Output() updateDto = new EventEmitter<Dto>();
  @Output() deleteDto = new EventEmitter<Dto>();
  @Output() saveInlineEdit = new EventEmitter<Dto>();
  @Output() savePreviewRow = new EventEmitter<Dto>();
  @Output() discardPreviewRow = new EventEmitter<Dto>();
  @Output() saveAllPreviewRows = new EventEmitter<void>();
  @Output() discardAllPreviewRows = new EventEmitter<void>();
  @Output() selectionChanged = new EventEmitter<Dto[]>();
  @Output() clipboardPasted = new EventEmitter<Dto[]>();
  @Output() clipboardCopied = new EventEmitter<Dto[]>();

  public isNavigationEnabled = computed<boolean>(() => {
    return !!this.options?.navigation?.withRowNavigation && !!this.options?.navigation?.rowNavigationCallback;
  });

  @ViewChild(TableComponent) private spxTable: TableComponent<Dto>;

  public columns: readonly IrdResolvedProperty<Dto>[] = [];
  public editPopoverConfig: (EditPopoverConfig & { property: IrdResolvedProperty<Dto> }) | null = null;
  public editingRow: Dto | null = null;
  public selectionModel: SpxSelectionModel<Dto> | undefined;
  public rowActions: RowAction<Dto>[] = [];

  protected readonly PAGE_SIZE_OPTIONS: number[] = PAGE_SIZE_OPTIONS;

  private selectionSubscription?: { unsubscribe(): void };
  private readonly destroyRef = inject(DestroyRef);
  private readonly errorHandler = inject(ErrorHandler);

  /** Re-runs the pure `irdFormat` pipe when the language or the translations change. */
  protected readonly translationsVersion = translationsVersion();

  /**
   * Rows with an in-flight handler, per action. Entries are removed when the handler settles.
   * A signal, so that a handler settling asynchronously marks the view for refresh — without it the
   * button would stay disabled until an unrelated change detection run happens to check this table.
   */
  private readonly pendingActions = signal<ReadonlyMap<RowAction<Dto>, ReadonlySet<Dto>>>(new Map());

  /**
   * Created once: `ngOnChanges` rebuilds the row actions on every input change, and a pending action
   * is looked up by identity — a fresh object per rebuild would drop its spinner and re-enable it.
   */
  private readonly editAction: RowAction<Dto> = {
    icon: 'edit',
    tooltip: 'EDIT_TOOLTIP',
    color: 'primary',
    handler: (event, dto) => {
      this.openUpdateDialog(event, dto);
      return EMPTY;
    },
  };

  private readonly deleteAction: RowAction<Dto> = {
    icon: 'delete',
    tooltip: 'DELETE_TOOLTIP',
    color: 'warn',
    handler: (event, dto) => {
      this.openDeleteDialog(event, dto);
      return EMPTY;
    },
  };

  constructor(private readonly actionsService: ActionsService) {}

  onRowAction(action: RowAction<Dto>, event: MouseEvent, dto: Dto): void {
    event.stopPropagation();

    if (this.isRowActionDisabled(action, dto)) {
      return;
    }

    this.markPending(action, dto);

    action
      .handler(event, dto)
      .pipe(
        take(1),
        finalize(() => this.releasePending(action, dto)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({ error: (error: unknown) => this.errorHandler.handleError(error) });
  }

  isRowActionVisible(action: RowAction<Dto>, dto: Dto): boolean {
    return action.visible?.(dto) !== false;
  }

  isRowActionDisabled(action: RowAction<Dto>, dto: Dto): boolean {
    return action.disabled?.(dto) === true || this.pendingActions().get(action)?.has(dto) === true;
  }

  visibleInlineRowActions(dto: Dto): RowAction<Dto>[] {
    return this.inlineRowActions.filter((action) => this.isRowActionVisible(action, dto));
  }

  visibleRowActions(dto: Dto): RowAction<Dto>[] {
    return this.rowActions.filter((action) => this.isRowActionVisible(action, dto));
  }

  hasActionsColumn(): boolean {
    return this.withActions || this.inlineRowActions.length > 0 || this.rowActions.length > 0;
  }

  /**
   * A page may rebuild its metadata once lookup values arrive, so the columns follow every new model —
   * and so do the row actions, whose edit action depends on whether any column edits inline.
   */
  ngOnChanges(): void {
    this.columns = this.metadata?.groups.flatMap((group) => group.properties) ?? [];
    this.rowActions = this.buildRowActions();
  }

  ngOnInit(): void {
    if (this.enableSelection) {
      this.selectionModel = new SpxSelectionModel<Dto>(true, []);
      this.selectionSubscription = this.selectionModel.changed.subscribe(() => {
        this.selectionChanged.emit(this.selectionModel.selected);
      });
    }
  }

  ngOnDestroy(): void {
    this.selectionSubscription?.unsubscribe();
  }

  getClipboard(): ClipboardDirective<Dto> | undefined {
    return this.spxTable?.clipboardDirective;
  }

  copyRowsViaDirective(rows: Dto[]): void {
    this.spxTable?.clipboardDirective?.copyRows(rows);
  }

  pasteViaDirective(): Promise<Dto[]> | undefined {
    return this.spxTable?.clipboardDirective?.pasteFromClipboard();
  }

  hasPreviewRows(): boolean {
    return this.previewRows.size > 0;
  }

  isPreviewRow(element: unknown): boolean {
    return this.previewRows.has(element);
  }

  openUpdateDialog(event: MouseEvent, dto: Dto): void {
    event.stopPropagation();
    this.updateDto.emit(dto);
  }

  openDeleteDialog(event: MouseEvent, dto: Dto): void {
    event.stopPropagation();
    this.deleteDto.emit(dto);
  }

  navigateTo(dto: Dto) {
    if (this.isNavigationEnabled() && !this.editingRow && !this.hasPreviewRows()) {
      this.options?.navigation?.rowNavigationCallback(dto);
    }
  }

  onSavePreviewRow(event: MouseEvent, dto: Dto): void {
    event.stopPropagation();
    this.savePreviewRow.emit(dto);
  }

  onDiscardPreviewRow(event: MouseEvent, dto: Dto): void {
    event.stopPropagation();
    this.discardPreviewRow.emit(dto);
  }

  onCellClick(event: MouseEvent, dto: Dto, property: IrdResolvedProperty<Dto>): void {
    if (!this.isPropertyEditable(property)) {
      return;
    }

    event.stopPropagation();
    this.openEditPopover(event, dto, property);
  }

  openEditPopover(event: MouseEvent, dto: Dto, property: IrdResolvedProperty<Dto>): void {
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();

    // Calculate position (center popover below the clicked cell)
    const top = rect.bottom + window.scrollY + 8;
    const left = rect.left + window.scrollX + rect.width / 2 - 150; // Center popover (assuming 300px width)

    this.editPopoverConfig = {
      value: dto[property.key],
      property: property,
      position: { top, left },
      selectOptions: this.selectOptionsFor(property),
    };

    this.editingRow = dto;
  }

  onPopoverSave(value: unknown): void {
    if (!this.editingRow || !this.editPopoverConfig) {
      return;
    }

    (this.editingRow as Record<string, unknown>)[this.editPopoverConfig.property.key] = value;

    this.saveInlineEdit.emit(this.editingRow);

    this.closeEditPopover();
  }

  onPopoverCancel(): void {
    this.closeEditPopover();
  }

  closeEditPopover(): void {
    this.editPopoverConfig = null;
    this.editingRow = null;
  }

  private selectOptionsFor(property: IrdResolvedProperty<Dto>): SelectOptions | undefined {
    if (!GenericTableComponent.CHOICE_TYPES.has(property.type)) {
      return undefined;
    }
    return (this.options?.[property.key] as SelectOptions) ?? this.selectOptionsFromMetadata(property);
  }

  private selectOptionsFromMetadata(property: IrdResolvedProperty<Dto>): SelectOptions | undefined {
    if (!property.allowedValues.length) {
      return undefined;
    }
    return {
      options: property.allowedValues.map((v) => v.value),
      labels: Object.fromEntries(property.allowedValues.map((v) => [v.value, v.label])),
    };
  }

  /**
   * The table's switch, the property's own `editable` fact, and a value the edit popover can edit —
   * for a choice, only once there are values to choose from (page options load asynchronously).
   */
  isPropertyEditable(property: IrdResolvedProperty<Dto>): boolean {
    return (
      !!this.metadata?.inlineEditing &&
      property.editable &&
      !property.array &&
      !GenericTableComponent.NOT_INLINE_EDITABLE.has(property.type) &&
      (!GenericTableComponent.CHOICE_TYPES.has(property.type) || !!this.selectOptionsFor(property)?.options?.length)
    );
  }

  hasInlineEditing(): boolean {
    return this.columns.some((column) => this.isPropertyEditable(column));
  }

  private markPending(action: RowAction<Dto>, dto: Dto): void {
    this.pendingActions.update((pending) => {
      const next = new Map(pending);
      next.set(action, new Set(pending.get(action)).add(dto));
      return next;
    });
  }

  private releasePending(action: RowAction<Dto>, dto: Dto): void {
    this.pendingActions.update((pending) => {
      const rows = pending.get(action);
      if (!rows?.has(dto)) {
        return pending;
      }

      const next = new Map(pending);
      const remaining = new Set(rows);
      remaining.delete(dto);

      if (remaining.size === 0) {
        next.delete(action);
      } else {
        next.set(action, remaining);
      }

      return next;
    });
  }

  private buildRowActions(): RowAction<Dto>[] {
    const actions: RowAction<Dto>[] = [];
    if (
      this.withActions &&
      this.crudSecureActions?.update &&
      this.actionsService.hasAction(this.crudSecureActions.update) &&
      (!this.hasInlineEditing() || this.withEditDialog)
    ) {
      actions.push(this.editAction);
    }
    if (this.withActions && this.crudSecureActions?.delete && this.actionsService.hasAction(this.crudSecureActions.delete)) {
      actions.push(this.deleteAction);
    }
    return [...actions, ...this.menuRowActions];
  }
}
